import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users, Zap, Lightbulb, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

export const jobOpenings = [
  {
    id: "swe-001",
    title: "Senior Backend Engineer (Node.js/Python)",
    location: "Remote (Global)",
    department: "Engineering",
    type: "Full-time",
    summary: "Join our core engineering team to design, build, and scale WebFiny's robust PDF processing APIs. You'll work with modern technologies and solve complex challenges in a collaborative environment.",
    responsibilities: [
      "Develop and maintain high-performance backend services for PDF manipulation.",
      "Design and implement scalable microservices architecture.",
      "Collaborate with frontend and product teams to deliver new features.",
      "Optimize API performance and ensure reliability.",
      "Write clean, well-tested, and maintainable code.",
      "Participate in code reviews and contribute to team best practices.",
      "Troubleshoot and resolve production issues."
    ],
    qualifications: [
      "5+ years of professional experience in backend development with Node.js or Python.",
      "Strong understanding of RESTful API design principles and microservices patterns.",
      "Proven experience with cloud platforms (AWS, GCP, or Azure), including serverless technologies.",
      "Proficiency with SQL and NoSQL databases (e.g., PostgreSQL, MongoDB, Redis).",
      "Experience with message queues (e.g., RabbitMQ, Kafka) and caching mechanisms.",
      "Familiarity with containerization technologies (Docker, Kubernetes) is a significant plus.",
      "Excellent problem-solving, analytical, and communication skills.",
      "Bachelor's degree in Computer Science or a related field, or equivalent practical experience."
    ],
    benefits: [
        "Competitive salary and performance bonuses.",
        "Comprehensive health, dental, and vision insurance.",
        "Generous paid time off and flexible work schedule.",
        "Remote-first work environment with a home office stipend.",
        "Opportunities for professional development and learning.",
        "Company retreats and team-building events."
    ],
    postedDate: "2025-06-01"
  },
  {
    id: "pm-002",
    title: "Product Manager - API Platform",
    location: "Remote (US/EU Timezones)",
    department: "Product",
    type: "Full-time",
    summary: "Drive the vision, strategy, and execution for WebFiny's API platform. You'll work closely with engineering, design, and marketing to deliver world-class developer experiences.",
    responsibilities: [
      "Define and own the product roadmap for our API services, aligning with company vision.",
      "Conduct market research, competitive analysis, and gather customer feedback to identify opportunities.",
      "Write detailed product specifications, user stories, and acceptance criteria.",
      "Collaborate with engineering and design teams throughout the product development lifecycle.",
      "Analyze product performance, user engagement, and iterate based on data insights.",
      "Work with marketing to develop go-to-market strategies for new features.",
      "Act as the primary point of contact for product-related inquiries."
    ],
    qualifications: [
      "3+ years of product management experience, specifically with developer-focused products or APIs.",
      "Strong technical background with a solid understanding of API technologies, SaaS, and cloud platforms.",
      "Excellent analytical, problem-solving, communication, and leadership skills.",
      "Proven ability to translate complex technical concepts into clear and concise product requirements.",
      "Experience with agile development methodologies (Scrum, Kanban).",
      "Ability to work effectively in a remote, globally distributed team.",
      "Bachelor's degree in a technical field or equivalent experience."
    ],
    benefits: [
        "Competitive salary and stock options.",
        "Full health benefits package.",
        "Flexible working hours and remote work options.",
        "Annual budget for conferences and professional growth.",
        "A key role in shaping a fast-growing product."
    ],
    postedDate: "2025-05-15"
  },
  {
    id: "mkt-003",
    title: "Developer Marketing Manager",
    location: "Remote (North America)",
    department: "Marketing",
    type: "Full-time",
    summary: "Lead our efforts to engage and grow WebFiny's developer community. You'll create compelling content, run campaigns, and build relationships with developers worldwide.",
    responsibilities: [
      "Develop and execute comprehensive developer marketing strategies to drive awareness and adoption.",
      "Create high-quality technical content, including blog posts, tutorials, documentation, and case studies.",
      "Manage and grow developer community channels (forums, social media, Discord/Slack).",
      "Plan and execute developer-focused marketing campaigns and events (online and offline).",
      "Analyze campaign performance and community engagement, providing regular reports and insights.",
      "Collaborate with product and engineering teams to ensure marketing materials are accurate and impactful.",
      "Identify and build relationships with key influencers and partners in the developer ecosystem."
    ],
    qualifications: [
      "4+ years of experience in developer marketing, developer advocacy, or a similar role.",
      "Strong understanding of developer audiences, API products, and the SaaS landscape.",
      "Excellent writing, verbal communication, and presentation skills.",
      "Proven experience with content creation (written, video, code samples) and community management.",
      "Familiarity with marketing analytics tools and CRM systems.",
      "Ability to work independently and manage multiple projects in a fast-paced environment.",
      "Technical background or experience with programming is highly desirable."
    ],
    benefits: [
        "Attractive salary and benefits package.",
        "Opportunity to build and lead a developer marketing function.",
        "Flexible, remote work environment.",
        "Budget for marketing tools and professional development.",
        "Travel opportunities for conferences and events (post-pandemic)."
    ],
    postedDate: "2025-06-05"
  }
];

const CareersPage = () => {
  const routeLocation = useLocation();
  const pageTitle = "Careers at WebFiny: Join Our Innovative Team";
  const pageDescription = "Explore exciting career opportunities at WebFiny. We're looking for talented individuals passionate about document automation, API technology, and building great products.";
  const canonicalUrl = `${window.location.origin}${routeLocation.pathname}`;

  const jobPostingSchema = jobOpenings.map(job => ({
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": `<p>${job.summary}</p><strong>Responsibilities:</strong><ul>${job.responsibilities.map(r => `<li>${r}</li>`).join('')}</ul><strong>Qualifications:</strong><ul>${job.qualifications.map(q => `<li>${q}</li>`).join('')}</ul><strong>Benefits:</strong><ul>${job.benefits.map(b => `<li>${b}</li>`).join('')}</ul>`,
    "identifier": {
      "@type": "PropertyValue",
      "name": "WebFiny Job ID",
      "value": job.id
    },
    "datePosted": job.postedDate,
    "validThrough": new Date(new Date(job.postedDate).setMonth(new Date(job.postedDate).getMonth() + 3)).toISOString().split('T')[0], 
    "employmentType": job.type.toUpperCase().replace('-', '_'), 
    "hiringOrganization": {
      "@type": "Organization",
      "name": "WebFiny",
      "sameAs": window.location.origin,
      "logo": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location.includes("Remote") ? undefined : job.location.split(",")[0],
        "addressCountry": job.location.includes("Global") ? undefined : (job.location.includes("US") || job.location.includes("North America") ? "US" : (job.location.includes("EU") ? "EU" : undefined)),
        "description": job.location 
      }
    },
    "jobLocationType": job.location.toLowerCase().includes("remote") ? "TELECOMMUTE" : undefined,
    "baseSalary": { 
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": 80000,
        "maxValue": 180000,
        "unitText": "YEAR"
      }
    }
  }));


  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                { "@type": "ListItem", "position": 2, "name": "Careers" }
              ]
            },
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          },
          ...jobPostingSchema
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30">
        <header className="py-16 md:py-24 text-center bg-gradient-to-r from-primary/10 via-transparent to-accent/10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Briefcase className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-6">
                Shape the Future with WebFiny
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                We're a passionate team building the next generation of document automation tools. If you're driven by innovation and want to make an impact, explore our open positions.
              </p>
            </motion.div>
          </div>
        </header>

        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="text-3xl font-semibold text-foreground mb-4">Why Join WebFiny?</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We offer a dynamic, remote-first culture where you can do your best work.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16 md:mb-20">
              {[
                { icon: <Zap className="w-8 h-8 text-primary" />, title: "Innovate & Impact", desc: "Work on cutting-edge PDF technology and solve real-world problems for developers and businesses." },
                { icon: <Users className="w-8 h-8 text-primary" />, title: "Collaborative Culture", desc: "Join a supportive, diverse team that values open communication and teamwork." },
                { icon: <Lightbulb className="w-8 h-8 text-primary" />, title: "Growth Opportunities", desc: "We invest in your professional development with learning resources and career advancement paths." },
                { icon: <MapPin className="w-8 h-8 text-primary" />, title: "Remote-First Flexibility", desc: "Enjoy the freedom to work from anywhere with a flexible schedule that promotes work-life balance." }
              ].map((perk, index) => (
                <motion.div
                  key={perk.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="bg-card p-6 rounded-xl shadow-lg text-center hover:shadow-accent/20 transition-shadow"
                >
                  <div className="p-3 bg-primary/10 rounded-full inline-block mb-4">
                    {perk.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{perk.title}</h3>
                  <p className="text-sm text-muted-foreground">{perk.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <h2 className="text-3xl font-semibold text-center text-foreground mb-10 md:mb-12">Current Openings</h2>
              {jobOpenings.length > 0 ? (
                <div className="space-y-8 max-w-3xl mx-auto">
                  {jobOpenings.map((job) => (
                    <motion.div
                      key={job.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      className="bg-card p-6 md:p-8 rounded-xl shadow-xl hover:shadow-primary/20 transition-shadow duration-300 group"
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                        <h3 className="text-2xl font-semibold text-foreground group-hover:text-primary transition-colors">{job.title}</h3>
                        <span className="text-sm text-muted-foreground mt-1 sm:mt-0 bg-muted px-2 py-1 rounded-md">{job.type}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground mb-4 space-x-4">
                        <span><MapPin className="w-4 h-4 inline mr-1 text-primary" /> {job.location}</span>
                        <span><Briefcase className="w-4 h-4 inline mr-1 text-primary" /> {job.department}</span>
                      </div>
                      <p className="text-muted-foreground mb-5 line-clamp-3">{job.summary}</p>
                       <Button 
                        variant="outline" 
                        asChild
                        className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                        aria-label={`Learn more and apply for ${job.title}`}
                      >
                        <Link to={`/careers/${job.id}`}>
                          Learn More & Apply <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-lg">
                  We don't have any open positions at the moment, but we're always looking for talented individuals.
                  Feel free to <Link to="/contact" className="text-primary hover:underline">send us your resume</Link>!
                </p>
              )}
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="mt-16 md:mt-20 text-center bg-gradient-to-r from-primary/80 to-accent/80 p-8 md:p-12 rounded-xl shadow-2xl text-primary-foreground"
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-4">Don't See a Fit?</h2>
              <p className="max-w-xl mx-auto mb-6 text-lg opacity-90">
                We're always interested in connecting with passionate and talented people. If you believe you can contribute to WebFiny's mission, send us your resume and tell us why!
              </p>
              <Button 
                variant="secondary" 
                size="lg" 
                asChild 
                className="bg-background/90 text-primary hover:bg-background shadow-lg"
              >
                <Link to="/contact?subject=GeneralApplication" aria-label="Submit a general application to WebFiny">
                  Submit General Application
                </Link>
              </Button>
            </motion.div>

          </div>
        </section>
      </div>
    </>
  );
};

export default CareersPage;